import delay from '@redux-saga/delay-p';
import {
  getReceivedFavorites,
  getSentFavorites,
  postFavorite,
} from 'affiny-core/modules/api/repositories/interactions/favorites/favorites.sagas';
import { handleAsyncStatus } from 'affiny-core/modules/asynchronousState/saga';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { ActionsOfType } from 'affiny-core/modules/helpers';
import { checkHasCompletedProfile } from 'affiny-core/modules/helpers/checkSaga';
import { getPaginatedData } from 'affiny-core/modules/helpers/pagination';
import { EMatchesActionType } from 'affiny-core/modules/matches/actions';
import { selectIsLoading } from 'affiny-core/redux/selectors/asyncStateSelectors';
import {
  selectReceivedFavoritesCurrentPage,
  selectReceivedFavoritesTotal,
  selectSentFavoritesCurrentPage,
  selectSentFavoritesTotal,
} from 'affiny-core/redux/selectors/favoritesSelectors';
import { selectSortMatchListLength } from 'affiny-core/redux/selectors/matchesSelectors';
import { selectIsSub } from 'affiny-core/redux/selectors/subscriptionsSelectors';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
  EFavoritesActionType,
  favoritesActions,
  IFavoritesAction,
} from './actions';

const SORT_MATCH_LIST_LENGTH_LIMIT_TO_REFRESH = 6;

const DATABASE_UPDATE_DELAY = 100;

export function* postFavoriteSaga(
  action: ActionsOfType<IFavoritesAction, EFavoritesActionType.POST_FAVORITE>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  try {
    yield call(
      postFavorite,
      action.payload.accountId,
      action.payload.ratingStatus,
      action.payload.ratingFeature,
      action.payload.eventOrigin
    );
    yield put(favoritesActions.postFavoriteSuccess(action.payload));

    if (action.payload.refreshList) {
      yield call(refreshListsWhenNotEnoughElements);
    }
  } catch (error) {
    yield put(favoritesActions.postFavoriteError(action.payload));
    throw error;
  } finally {
    if (action.payload.resolve) {
      yield call(action.payload.resolve);
    }
  }
}

export function* refreshListsWhenNotEnoughElements() {
  // Add delay to wait for database update of match list
  yield call(delay, DATABASE_UPDATE_DELAY);

  const sortMatchListLength = yield select(selectSortMatchListLength);
  const sortMatchListIsLoading = yield select(
    selectIsLoading,
    EAsyncEventTypes.SORT_MATCH_LIST
  );

  if (
    !sortMatchListIsLoading &&
    sortMatchListLength <= SORT_MATCH_LIST_LENGTH_LIMIT_TO_REFRESH
  ) {
    yield put({
      type: EMatchesActionType[`GET_SORT_MATCH_LIST`],
      payload: { reset: false },
    });
  }
}

export function* getReceivedFavoritesSaga(
  action: ActionsOfType<
    IFavoritesAction,
    EFavoritesActionType.GET_RECEIVED_FAVORITES
  >
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const isSub = yield select(selectIsSub);

  yield call(getPaginatedData as any, {
    pageSelector: selectReceivedFavoritesCurrentPage,
    totalSelector: selectReceivedFavoritesTotal,
    reset: action.payload.reset,
    apiCall: getReceivedFavorites,
    resetAction: favoritesActions.resetReceivedFavorites,
    successAction: favoritesActions.getReceivedFavoritesSuccess,
    successActionParameters: [isSub],
  });
}

export function* getSentFavoritesSaga(
  action: ActionsOfType<
    IFavoritesAction,
    EFavoritesActionType.GET_SENT_FAVORITES
  >
) {
  if (!(yield* checkHasCompletedProfile())) return;

  yield call(getPaginatedData as any, {
    pageSelector: selectSentFavoritesCurrentPage,
    totalSelector: selectSentFavoritesTotal,
    reset: action.payload.reset,
    apiCall: getSentFavorites,
    resetAction: favoritesActions.resetSentFavorites,
    successAction: favoritesActions.getSentFavoritesSuccess,
  });
}

export function* favoritesRootSaga() {
  yield all([
    takeLatest(
      EFavoritesActionType.POST_FAVORITE,
      handleAsyncStatus(postFavoriteSaga, EAsyncEventTypes.POST_FAVORITES)
    ),
    takeLatest(
      EFavoritesActionType.GET_RECEIVED_FAVORITES,
      handleAsyncStatus(
        getReceivedFavoritesSaga,
        EAsyncEventTypes.RECEIVED_FAVORITES
      )
    ),
    takeLatest(
      EFavoritesActionType.GET_SENT_FAVORITES,
      handleAsyncStatus(getSentFavoritesSaga, EAsyncEventTypes.SENT_FAVORITES)
    ),
  ]);
}
